import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import QrButton from '../../../Shared/QrButton';
import FilterProjectRow from '../../../Shared/Tables/FilterProjectRow';
import EditButton from './EditButton';
import { Search, ChevronLast, ChevronFirst, ChevronRight, ChevronLeft, ArrowDownAZ } from 'lucide-react';
import { TableContainer, IconButton, Table, TableFooter, TablePagination, TableRow, Paper, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';


function TablePaginationActions(props) {
    const theme = useTheme();
    const classes = useStyles();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <div className={classes.footer}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <ChevronLast /> : <ChevronFirst />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <ChevronFirst /> : <ChevronLast />}
            </IconButton>
        </div>
    );
}

TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
};



export default function TableManage(props) {
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const currentDate = new Date();
    // Avoid a layout jump when reaching the last page with empty rows.
    // const emptyRows =
    //   page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.rows.length) : 0;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const classes = useStyles();
    var measurementTypes = props.types;
    function reformatDate(dateStr) {
        let dArr = dateStr.split("-");
        return dArr[2] + "/" + dArr[1] + "/" + dArr[0];
    }
    const datesAreOnSameDay = (first, second) =>
        first.getFullYear() === second.getFullYear() &&
        first.getMonth() === second.getMonth() &&
        first.getDate() === second.getDate();


    function dateRange(a_start, a_end, b_start, b_end) {
        if (a_start <= b_start && b_start <= a_end) return true; // b starts in a
        if (a_start <= b_end && b_end <= a_end) return true; // b ends in a
        if (b_start < a_start && a_end < b_end) return true; // a in b

        if (datesAreOnSameDay(a_start, b_start)) return true;
        if (datesAreOnSameDay(b_end, a_end)) return true;
        return false;
    }

    return (
        <TableContainer style={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }} component={Paper}>
            <Table size="sm" key={"table"}>
                <thead className={classes.headStyle} key={"thead"}>
                    <tr className={classes.tableHeadRow} key={"trow"}>
                        <th className={classes.tableHead} style={{ width: '20%' }}>
                            <span className={classes.titleSearch}>
                                <div className={classes.icons}>
                                    <Tooltip title={t('tableManage.alphabeticalOrder')} placement="top" arrow>
                                        <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting('companyName')}>
                                            <ArrowDownAZ />
                                        </IconButton>
                                    </Tooltip>
                                    {t('tableManage.customer')}
                                    <Tooltip title={t('tableManage.searchProject')} placement="top" arrow>
                                        <IconButton style={{ borderRadius: 0 }} onClick={() => props.toggleFilter()}>
                                            <Search />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </span>
                        </th>
                        <th className={classes.tableHead} style={{ width: '20%' }}>
                            <span className={classes.titleSearch}>
                                <div className={classes.icons}>
                                    <Tooltip title={t('tableManage.alphabeticalOrder')} placement="top" arrow>
                                        <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting('projectName')}>
                                            <ArrowDownAZ />
                                        </IconButton>
                                    </Tooltip>
                                    {t('project.project')}
                                    <Tooltip title={t('tableManage.searchProject')} placement="top" arrow>
                                        <IconButton style={{ borderRadius: 0 }} onClick={() => props.toggleFilter()}>
                                            <Search />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </span>
                        </th>
                        <th className={classes.tableHead} style={{ width: '20%' }}>
                            <span className={classes.titleSearch}>
                                <div className={classes.icons}>
                                    <Tooltip title={t('tableManage.alphabeticalOrder')} placement="top" arrow>
                                        <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting('resourceName')}>
                                            <ArrowDownAZ />
                                        </IconButton>
                                    </Tooltip>
                                    {t('project.registrationPoint')}
                                    <Tooltip title={t('tableManage.searchRegistrationPoint')} placement="top" arrow>
                                        <IconButton style={{ borderRadius: 0 }} onClick={() => props.toggleFilter()}>
                                            <Search />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </span>
                        </th>
                        <th className={classes.tableHead} style={{ width: '20%' }}>
                            <span className={classes.titleSearch}>
                                <div className={classes.icons}>
                                    <Tooltip title={t('tableManage.sortDescending')} placement="top" arrow>
                                        <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting('fromDate')}>
                                            <ArrowDownAZ />
                                        </IconButton>
                                    </Tooltip>
                                    {t('results.dateRange')}

                                </div>
                            </span>
                        </th>
                        <th align="center" className={classes.tableHead} style={{ width: '3%', textAlign: 'center' }}>{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody key={"tbody"}>
                    {props.filterOn && <FilterProjectRow rows={props.rows} filters={props.filters} filterProjects={props.filterProjects} updateFilters={props.updateFilters}></FilterProjectRow>}
                    {(props.rows !== undefined && props.rows.length) > 0 ?
                        <React.Fragment>
                            {(rowsPerPage > 0
                                ? props.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                : props.rows
                            ).map((row) => (
                                <tr className={classes.tableBodyRow} key={row.projectId}>
                                    <td>{row.companyName}</td>
                                    <td>{row.projectName}</td>
                                    <td>{row.resourceName}</td>
                                    <td>{reformatDate(row.fromDate.slice(0, 10)) + " - " + reformatDate(row.toDate.slice(0, 10))}</td>
                                    <td className={classes.actionButtons}>

                                        <QrButton disabled={!dateRange(new Date(row.fromDate), new Date(row.toDate), new Date(currentDate), new Date(currentDate))} projectName={row.projectName} resourceName={row.resourceName} QrCode={row.qrCode} size='large' />

                                        <EditButton size='large' data={
                                            ({
                                                projectQr: row.qrCode,
                                                projectName: row.projectName,
                                                company: row.companyName,
                                                companyId: row.companyId,
                                                resource: row.resourceName,
                                                fromDate: row.fromDate,
                                                toDate: row.toDate,
                                                projectId: row.projectId,
                                                details: row.details,
                                                types: measurementTypes,
                                            })} />
                                    </td>
                                </tr>
                            ))}
                        </React.Fragment>
                        :
                        <tr className={classes.tableBodyRow}>
                            <td>{t('tableManage.noProjectsAvailable')}</td>
                            <td>-</td>
                            <td>-</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                    }


                </tbody>
                <TableFooter className={classes.tableFooter}>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[100, 200, 500, { label: t('tableManage.all'), value: -1 }]}
                            colSpan={12}
                            count={props.rows !== null && props.rows !== undefined ? props.rows.length : 0}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}
const useStyles = makeStyles((theme) => ({
    tableHead: {
        height: '42px',
        borderRight: '2px solid #e6ecf0',
    },
    titleSearch: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    headStyle: {
        backgroundColor: "#e0dce4",
    },
    tableBodyRow: {
        justifyContent: "center",
        textAlign: "center",
    },
    actionButtons: {
        padding: '0px !important',
        textAlign: "center",
        display: "flex",
        justifyContent: "center"
    },
    tableFooter: {
        width: '100%'
    },
    footer: {
        display: 'flex',
    },
    icons: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }
}));
